/**
 * Dev-sync J5 — the chart→rating-dialog re-rate handoff contract.
 *
 * ONE localStorage key: `psy_pending_rerate` (psychrometric_chart.js:61-71),
 * written by jumpToSection right before it opens the rating dialog
 * (js:1489-1499) and read+cleared by whichever selector's gate matches as it
 * initializes — ★ the clear happens INSIDE each selector's match gate
 * (coil.selector.js:619, fan:693, filter:124, evap_pack:201, gas_heater:326,
 * ewheel:604, phx:872), so a non-matching selector leaves the key for the
 * one it was meant for. Byte-compatible with `fn.local_storage.set` (objects
 * are JSON.stringified — js_scripts/local_storage.js:9-14).
 *
 * Value shape = the chart's stale_section_ids entry with both seasons'
 * fields merged (js:1473-1499): `{section_id, target, component_id, fields}`
 * — summer's keys are BARE (`eadb_temp`/`eawb_temp`, or unsuffixed
 * `oa_*`/`ra_*` on a bridge) and never collide with winter's explicit
 * `_winter` keys, so a plain merge is safe.
 *
 * TWO gate flavors (measured in the legacy selectors):
 *  - SECTION-scoped (coil/fan/filter/evap_pack/gas_heater): fields present,
 *    the selector's own section matches `section_id` (String compare), and
 *    `target !== 'component'`.
 *  - COMPONENT-scoped (energy wheel / plate HX — the airstream BRIDGES):
 *    `target === 'component'`, `component_id` matches the selector's model,
 *    and `section_id` matches EITHER of the bridge's two sections
 *    (ewheel:579-582, phx:432-436). Board mode only (`!self.header`).
 */

export interface PendingRerate {
  section_id: number | string;
  target?: 'section' | 'component';
  component_id?: number | null;
  fields?: Record<string, number>;
}

export const PSY_PENDING_RERATE_KEY = 'psy_pending_rerate';

export function readPendingRerate(): PendingRerate | null {
  try {
    const raw = window.localStorage.getItem(PSY_PENDING_RERATE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as PendingRerate;
  } catch {
    return null;
  }
}

export function writePendingRerate(p: PendingRerate): void {
  try {
    window.localStorage.setItem(PSY_PENDING_RERATE_KEY, JSON.stringify(p));
  } catch {
    /* quota/private mode */
  }
}

export function clearPendingRerate(): void {
  try {
    window.localStorage.removeItem(PSY_PENDING_RERATE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * The SECTION-scoped consume: gate → clear → return a fields copy, or null
 * (key left untouched) when the gate misses.
 */
export function takeSectionPendingRerate(
  sectionId: number | string | null | undefined
): Record<string, number> | null {
  const p = readPendingRerate();
  if (
    !p ||
    !p.fields ||
    sectionId == null ||
    String(sectionId) !== String(p.section_id) ||
    p.target === 'component'
  ) {
    return null;
  }
  clearPendingRerate();
  return { ...p.fields };
}

/**
 * The COMPONENT-scoped consume (bridges): gate → clear → fields copy.
 * `sectionIds` = the bridge's own section_id/section_2_id pair.
 */
export function takeComponentPendingRerate(
  componentId: number | string | null | undefined,
  sectionIds: Array<number | string | null | undefined>
): Record<string, number> | null {
  const p = readPendingRerate();
  if (
    !p ||
    !p.fields ||
    p.target !== 'component' ||
    componentId == null ||
    p.component_id == null ||
    String(componentId) !== String(p.component_id) ||
    !sectionIds.some((sid) => sid != null && String(sid) === String(p.section_id))
  ) {
    return null;
  }
  clearPendingRerate();
  return { ...p.fields };
}
